import { useMemo } from "react";
import type { ProductionHistoryWindow } from "@/features/devices/types";
import { binBars, bucketMsForWindow, projectChartPoints, resolveChartConfig } from "../../chartWidgets";
import type { DashboardWidget, ResolvedWidgetValue } from "../../types";
import type { WidgetHistoryState } from "../../useProductionHistories";
import { describeSignalDiagnostic } from "./diagnosticMeaning";
import { formatWidgetNumber, formatWidgetRelative } from "./format";
import { HeroCard, HeroMeaning, HeroWindowPicker } from "./HeroCard";
import { TimeSeriesChart, type TimeSeriesPoint } from "./TimeSeriesChart";
import { TrendStats, useRateWindowStats } from "./TrendStats";
import { windowSpan } from "./windowPhrase";
import { describeQuality, effectiveQuality } from "./quality";

/** O que o valor de cada ponto E, para o tooltip dizer em vez de sugerir. */
const VALUE_LABEL: Record<string, string> = {
  rate: "média",
  throughput: "produção",
  cumulative: "acumulado",
};

interface ChartWidgetProps {
  widget: DashboardWidget;
  value: ResolvedWidgetValue;
  history?: WidgetHistoryState;
  editable?: boolean;
  onConfigure?: () => void;
  /** Desfaz o "Gerar grafico": so existe em card que nasceu de um. */
  onHideChart?: () => void;
  onChangeWindow?: (window: ProductionHistoryWindow) => void;
}

const SERIES_LABEL: Record<string, string> = {
  rate: "Ritmo",
  throughput: "Total por período",
  cumulative: "Acumulado",
};

function seriesLabel(widget: DashboardWidget, series: string): string {
  if (series === "rate" && widget.settings.signalKind === "instantaneous") {
    return "Leitura no tempo";
  }
  if (series === "rate" && widget.settings.signalKind === "state") {
    return "Código no tempo";
  }
  if (series === "rate" && widget.settings.signalKind === "cumulative") {
    return "Valor acumulado no tempo";
  }
  return SERIES_LABEL[series] ?? "Série";
}

const WINDOW_LABEL: Record<string, string> = {
  "6h": "6 h",
  "12h": "12 h",
  "24h": "24 h",
  "48h": "48 h",
  "7d": "7 dias",
  "30d": "30 dias",
};

/**
 * Gráfico genérico no cartão herói — combos não cobertos pelos heróis de
 * ritmo (linha) e produção (barras): ex.: acumulado em linha, ou a linha de
 * uma grandeza INSTANTÂNEA — que desde a semântica de `kind` deixou de cair no
 * herói de ritmo. O headline é o herói e a série temporal é a FACE embutida.
 *
 * Por receber esse desvio, este cartão precisa do MESMO seletor de período dos
 * demais: sem ele, declarar `kind` numa fonte tirava do operador o controle de
 * janela que a mesma fonte tinha antes — o tipo de regressão silenciosa que a
 * tabela de capacidades existe para evitar (`line-chart` declara `window: true`).
 */
export function ChartWidget({
  widget,
  value,
  history,
  editable,
  onConfigure,
  onHideChart,
  onChangeWindow,
}: ChartWidgetProps) {
  const config = resolveChartConfig(widget);
  const status = history?.status ?? "loading";
  // `reloading` = troca de janela em curso: a série ANTERIOR continua visível
  // (atenuada) até a nova chegar, como nos heróis de ritmo e produção.
  const busy = history?.status === "reloading";
  const data =
    history?.status === "ready" || history?.status === "reloading" ? history.data : null;
  const unit = widget.settings.unit?.trim() || null;
  const decimals = widget.settings.decimals ?? (config.series === "rate" ? 1 : 0);
  // Enquanto recarrega, eixo e rótulo seguem a janela do DADO exibido, não a
  // recém-pedida — projetar pontos de 24 h como se fossem de 7 d deslocaria a
  // curva inteira.
  const dataWindow = (data?.window ?? config.window) as ProductionHistoryWindow;
  const windowLabel = WINDOW_LABEL[dataWindow] ?? dataWindow;
  const aggregation = data && config.series === "rate"
    ? ` · médias por ${data.resolution === "1m" ? "minuto" : data.resolution === "1h" ? "hora" : "dia"}` : "";

  // Buckets sem valor sobrevivem como `value: null` — a linha interrompe e a
  // barra vira marca na base, em vez de o ponto sumir e o gráfico fingir
  // continuidade sobre uma falha de leitura.
  const points = useMemo(() => {
    const rawPoints: TimeSeriesPoint[] = data ? projectChartPoints(data.points, config.series, dataWindow) : [];
    return config.kind === "bar" ? binBars(rawPoints, 48, "last", bucketMsForWindow(dataWindow)) : rawPoints;
  }, [data, config.series, config.kind, dataWindow]);
  const hasPlottable = points.some((point) => point.value !== null);
  // Media da janela so faz sentido para a serie de LEITURA: media de um
  // acumulado ou de producao por periodo nao responde pergunta nenhuma.
  const stats = useRateWindowStats(config.series === "rate" ? data : null);
  // Média de bucket não é leitura atual. O destaque vem da telemetria viva;
  // sem leitura, nunca reutiliza o último ponto bom do histórico.
  // Throughput é a exceção explícita: representa o total da janela passada.
  const quality = effectiveQuality(value);
  const hasCurrent = quality === "live" && value.isAvailable &&
    value.numericValue !== null && Number.isFinite(value.numericValue);
  const headline =
    config.series === "throughput" && data
      ? data.total
      : hasCurrent ? value.numericValue : null;
  const diagnosed = describeSignalDiagnostic(value.diagnostic);
  const meaning = diagnosed ?? (quality !== "live"
    ? { tone: "warn" as const, text: describeQuality(quality).title } : null);
  // Rodapé pela leitura REAL (não o fim da janela), coerente com os heróis.
  const rel = value.measuredAt ? formatWidgetRelative(value.measuredAt) : null;
  const footText =
    !data
      ? null
      : hasCurrent
        ? `Atualizado ${rel ?? "agora"}`
        : rel
          ? `Última leitura ${rel}`
          : null;
  // Media/extremos do periodo moram no rodape, ao lado do "Atualizado".
  const footnote =
    data && hasPlottable && stats.mean !== null ? (
      <>
        {footText ? <span>{footText}</span> : null}
        <TrendStats stats={stats} unit={unit} decimals={decimals} window={dataWindow} />
      </>
    ) : footText;

  return (
    <HeroCard
      title={widget.title}
      caption={`${seriesLabel(widget, config.series)} · ${windowLabel}`}
      tone={meaning?.tone === "danger" ? "danger" : meaning || status === "error" ? "warn" : "ok"}
      editable={editable}
      onConfigure={onConfigure}
      onHideChart={onHideChart}
      footnote={footnote}
    >
      <div className="w-hero__value">
        {formatWidgetNumber(headline, decimals)}
        {unit ? <span className="w-hero__unit">{unit}</span> : null}
      </div>

      {meaning ? <HeroMeaning tone={meaning.tone}>{meaning.text}</HeroMeaning> : null}

      <div
        className={busy ? "w-hero__trend w-hero__trend--busy" : "w-hero__trend"}
        aria-busy={busy || undefined}
      >
        <div className="w-hero__trend-hd">
          <span>Ao longo {windowSpan(dataWindow)}{aggregation}</span>
          {onChangeWindow ? (
            <HeroWindowPicker
              value={config.window}
              busy={busy}
              onChange={onChangeWindow}
              ariaLabel={`Período de ${widget.title}`}
            />
          ) : null}
        </div>
        {data && hasPlottable ? (
          <TimeSeriesChart
            points={points}
            kind={config.kind}
            window={dataWindow}
            unit={unit ?? undefined}
            decimals={decimals}
            yScale={config.yScale}
            min={widget.settings.min}
            max={widget.settings.max}
            thresholds={widget.settings.thresholds}
            title={widget.title}
            reference={stats.reference}
            valueLabel={VALUE_LABEL[config.series]}
          />
        ) : (
          <p className="w-hero__trend-empty">
            {status === "unavailable"
              ? "Ative o Timescale para ver a série."
              : status === "error"
                ? "Não foi possível carregar a série."
                : status === "ready"
                  ? "Ainda não há amostras nesta janela."
                  : "Carregando série…"}
          </p>
        )}
      </div>
    </HeroCard>
  );
}
